import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';

import { AdminTablePlaceholderRows } from 'src/components/Admin/AdminTablePlaceholder';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import { LOAD_MORE, REFETCH } from 'src/util/constants';

import AdminMeterTradeRulesItem from './AdminMeterTradeRulesItem';

const messages = defineMessages({
  paginationError: {
    id: 'admin.admin_meter.admin_meter_trade_rules_table.pagination_error.message',
    defaultMessage: 'Failed to load more trade rules. Please try again.',
  },
});

/**
 * Skeleton rows rendered behind the pagination error modal, inside the loaded
 * table, to stand in for the page that failed to load.
 * @returns {React.ReactElement} Skeleton table rows.
 */
function PaginationErrorRows() {
  return <AdminTablePlaceholderRows columnCount={5} rowCount={3} />;
}

/**
 * Table of a meter's trade rules with a load-more footer and an inline retry
 * modal for load-more failures.
 * @param {object} props - Component props.
 * @param {object|null} props.meter - The meter whose trade rules are listed.
 * @param {object|null} props.rules - Relay fragment reference for the trade rule connection.
 * @param {Function} props.moreFunc - Loads the next page; also the modal's retry action.
 * @param {boolean} props.hasMore - Whether another page is available.
 * @param {string|null} props.updating - Current update kind (`LOAD_MORE` or `REFETCH`).
 * @param {Error|null} props.paginationError - The last load-more error, if any.
 * @param {Function} props.paginationErrorCloseFunc - Dismisses the pagination error modal.
 * @returns {React.ReactComponentElement} AdminMeterTradeRulesTable component.
 */
function AdminMeterTradeRulesTable({
  meter = null, rules = null, moreFunc, hasMore, updating = null,
  paginationError = null, paginationErrorCloseFunc,
}) {
  const intl = useIntl();

  if (!meter) {
    return <Loading />;
  }

  const { tradePointId, property } = meter;
  const { uuid: propertyId } = property;

  const trades = (!rules || !rules.edges)
    ? [] : rules.edges.map((el) => (el.node));

  const headerClauses = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.clauses.label', defaultMessage: 'Clauses' });

  // Table header translations

  const headerTradeRuleId = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.id.label', defaultMessage: 'ID' });
  const headerTradeRuleIdTitle = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.id.title', defaultMessage: 'identifier' });
  const headerTradeRuleColespanSeparator = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.colspan.separator.', defaultMessage: '/' });
  const headerTradeRuleDirection = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.direction.label', defaultMessage: 'Direction' });
  const headerTradeRuleType = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.type.label', defaultMessage: 'Type' });
  const headerTradeRuleState = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.state.label', defaultMessage: 'State' });

  const headerIdDirectionTypeState = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_trade_rules_table.header.id_direction_type_state.label', defaultMessage: '{headerTradeRuleId} {headerTradeRuleColespanSeparator} {headerTradeRuleDirection} {headerTradeRuleColespanSeparator} {headerTradeRuleType} {headerTradeRuleColespanSeparator} {headerTradeRuleState}' }, {
    headerTradeRuleId: <abbr title={headerTradeRuleIdTitle}>{headerTradeRuleId}</abbr>,
    headerTradeRuleColespanSeparator,
    headerTradeRuleDirection,
    headerTradeRuleType,
    headerTradeRuleState,
  });

  const headercounterpartyTrader = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_show.admin_meter_trade_rules_table.counterparty_trader_header',
    defaultMessage: 'Counterparty Trader',
  });
  const headercounterpartyTradePoint = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_show.admin_meter_trade_rules_table.counterparty_trade_point_header',
    defaultMessage: 'Counterparty Trade Point',
  });
  const headerValidity = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_show.admin_meter_trade_rules_table.validity_header',
    defaultMessage: 'Validity',
  });

  // Load more button translations
  const loadMoreStateLoadMore = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_show.admin_meter_trade_rules_table.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const loadMoreStateNoMore = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_show.admin_meter_trade_rules_table.load_more_button.state_no_more.label',
    defaultMessage: 'No more trade rules to load',
  });

  return (
    <div className="mt-4 mb-4 card">
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">
                {headerIdDirectionTypeState}
              </th>
              <th scope="col">{headercounterpartyTrader}</th>
              <th scope="col">{headercounterpartyTradePoint}</th>
              <th scope="col">{headerClauses}</th>
              <th scope="col">{headerValidity}</th>
            </tr>
          </thead>
          <tbody>
            {updating !== REFETCH && trades
              && trades.map((tradeRule) => (
                <AdminMeterTradeRulesItem
                  tradePointId={tradePointId}
                  rule={tradeRule}
                  propertyId={propertyId}
                  key={tradeRule.uuid}
                />
              ))}
            {(updating === LOAD_MORE || updating === REFETCH) && (
              <tr>
                <td colSpan={5}>
                  <Loading />
                </td>
              </tr>
            )}
            {/* The modal renders through a portal; only its skeleton rows render here. */}
            {paginationError && (
              <PlaceholderErrorModal
                bodyText={intl.formatMessage(messages.paginationError)}
                error={paginationError}
                onCancelFunc={paginationErrorCloseFunc}
                onRetryFunc={moreFunc}
                placeholderComponent={PaginationErrorRows}
                retryDisabled={updating === LOAD_MORE}
              />
            )}
          </tbody>
        </table>
      </div>
      <div className="card-footer">
        <LoadMoreButton
          copyLoadMore={loadMoreStateLoadMore}
          copyNoMore={loadMoreStateNoMore}
          onClick={moreFunc}
          hasNext={updating === !LOAD_MORE || hasMore}
        />
      </div>
    </div>
  );
}

AdminMeterTradeRulesTable.propTypes = {
  meter: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  rules: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  moreFunc: PropTypes.func.isRequired,
  hasMore: PropTypes.bool.isRequired,
  updating: PropTypes.string,
  paginationError: PropTypes.instanceOf(Error),
  paginationErrorCloseFunc: PropTypes.func.isRequired,
};

export default createFragmentContainer(
  AdminMeterTradeRulesTable,
  {
    rules: graphql`
      fragment AdminMeterTradeRulesTable_rules on TradeRuleConnection {
        edges {
          cursor
          node {
            uuid
            ...AdminMeterTradeRulesItem_rule
          }
        }
      }
    `,
  },
);
